'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';
import { StaggeredText } from '@/components/ui/StaggeredText';
import { TypographyH2, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import SectionBase from '../section-base';
import {
  TESTIMONIALS_TITLE,
  TESTIMONIALS,
  type Testimonial,
} from './consts';

const SECTION_ID = 'testimonials';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <motion.div
      className={cls`flex flex-col gap-6 border-l-2 border-primary pl-6 py-4`}
      {...cardMotionProps}
    >
      <Quote className="text-primary size-10 fill-primary" />

      <TypographySmall className="text-foreground leading-relaxed">
        {testimonial.quote}
      </TypographySmall>

      <span className="font-prata text-base text-foreground">
        &mdash; {testimonial.author}
      </span>
    </motion.div>
  );
}

function TestimonialsSection() {
  return (
    <SectionBase
      id={SECTION_ID}
      containerProps={{ id: SECTION_ID }}
      background="bg-background"
    >
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        {/* Title */}
        <TypographyH2 className={cls`text-center border-b-0`}>
          <StaggeredText staggerChildren={0.03} delay={0.1}>
            {TESTIMONIALS_TITLE}
          </StaggeredText>
        </TypographyH2>

        {/* 3-column testimonial cards */}
        <div className={cls`grid grid-cols-1 md:grid-cols-3 gap-8 w-full`}>
          {TESTIMONIALS.map(testimonial => (
            <TestimonialCard
              key={testimonial.author}
              testimonial={testimonial}
            />
          ))}
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default TestimonialsSection;
